import type { FlowEmail, FlowStatus, FlowSummary } from '@/lib/api/email-flows/types';
import { convertDelayToSeconds } from './emailDelay.logic';
import type { DelayUnit } from '../types/email.types';
import { FLOW_TRIGGER } from '@/lib';

/**
 * Check if email updates are disabled
 * Updates are disabled if the email has been sent (analytics.sent > 0)
 *
 * @param email - The flow email object
 * @returns True if updates should be disabled
 */
export function areUpdatesDisabled(email: FlowEmail): boolean {
    // If email has been sent, disable updates
    if (email.analytics && email.analytics.sent && email.analytics.sent > 0) {
        return true;
    }

    return false;
}

/**
 * Check if a flow email can be modified based on timing constraints
 * This replicates the backend's check_if_flow_modifiable logic
 *
 * For TIME-triggered flows:
 * - Cannot modify if the broadcast has already started
 * - When publishing, the delay must be sufficient to allow the email to be sent
 * - This prevents scheduling emails that would be sent in the past
 *
 * @param flow - The flow data
 * @param delayValue - The delay value from the form
 * @param delayUnit - The delay unit from the form
 * @param status - The status being saved ('draft' or 'published')
 * @returns Object with modifiable flag and optional error message
 */
export function checkEmailModifiability(
    flow: FlowSummary | null,
    delayValue: string,
    delayUnit: DelayUnit,
    status: FlowStatus
): { modifiable: boolean; message?: string } {
    if (!flow || flow.trigger !== FLOW_TRIGGER.TIME) {
        return { modifiable: true };
    }

    // Check if the broadcast has already started
    // Prevent ANY modifications (draft or published) if broadcast has started
    if (flow.data?.started === true) {
        return {
            modifiable: false,
            message: 'A broadcast cannot be modified after it has started.',
        };
    }

    // Only check timing constraints when publishing
    // Drafts can be saved even if timing would prevent publishing
    if (status !== 'published') {
        return { modifiable: true };
    }

    // Convert delay to seconds
    const delayInSeconds = convertDelayToSeconds(parseInt(delayValue, 10), delayUnit);

    // Get flow start time
    const flowStartTime =
        flow.data?.specific_time?.date && flow.data?.specific_time?.time
            ? new Date(`${flow.data.specific_time.date}T${flow.data.specific_time.time}`).getTime() / 1000
            : null;

    if (!flowStartTime) {
        // If no start time, allow modification
        return { modifiable: true };
    }

    // Calculate when this email would be sent
    const emailSendTime = flowStartTime + delayInSeconds;
    const currentTime = Date.now() / 1000;

    // Check if the email would be sent in the past
    if (emailSendTime < currentTime) {
        return {
            modifiable: false,
            message:
                'Cannot publish email: the scheduled send time would be in the past. Please adjust the delay or flow start time.',
        };
    }

    // Additional check: ensure there's at least a minimum buffer (e.g., 10 minutes)
    const minimumBufferSeconds = 10 * 60; // 10 minutes
    if (emailSendTime < currentTime + minimumBufferSeconds) {
        return {
            modifiable: false,
            message:
                'Cannot publish email: the scheduled send time must be at least 10 minutes in the future. Please adjust the delay.',
        };
    }

    return { modifiable: true };
}
